/* NotificationBell — header widget for opt-in Web Push notifications.
 *
 * UX states (resolved real-time on every click — not cached):
 *  - default  → outlined bell, click triggers permission prompt
 *  - granted but no sub → bell, click subscribes silently
 *  - granted + subscribed → red BellRing, click unsubscribes
 *  - denied   → BellOff, click shows recovery instructions
 *  - unsupported → component not rendered
 *
 * Self-heals: on load, if browser says "granted" but the backend doesn't have
 * an active subscription, we silently re-subscribe.
 * Stays in sync: listens to the Permissions API change event AND tab focus
 * events so the icon mirrors browser-level changes without a page reload.
 */
import React, { useEffect, useState } from "react";
import { Bell, BellRing, BellOff } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported,
  currentPermission,
  getCurrentSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  updateProvinceForCurrent,
} from "@/lib/push";
import { useProvince } from "@/contexts/ProvinceContext";

async function readBrowserState() {
  if (!isPushSupported()) return { permission: "unsupported", hasSub: false };
  const perm = currentPermission();
  let sub = null;
  try { sub = await getCurrentSubscription(); } catch { /* ignore */ }
  return { permission: perm, hasSub: !!sub };
}

export default function NotificationBell() {
  const { selected } = useProvince();
  const [permission, setPermission] = useState("default");
  const [hasSub, setHasSub] = useState(false);
  const [busy, setBusy] = useState(false);
  const selectedProvinceId = selected?.id || null;

  // Bootstrap + watch browser-side state.
  useEffect(() => {
    let cancelled = false;

    async function applyState() {
      const { permission: p, hasSub: s } = await readBrowserState();
      if (cancelled) return;
      setPermission(p);
      setHasSub(s);
      // Self-heal: granted in browser but no active sub → silently subscribe.
      // (Province sync happens in the other effect after this lands.)
      if (p === "granted" && !s) {
        try {
          await subscribeToPush({ provinceId: null });
          if (!cancelled) setHasSub(true);
        } catch { /* best-effort */ }
      }
    }

    applyState();

    // Permissions API change listener (Chrome/Edge/Firefox).
    let permStatus = null;
    let permHandler = null;
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "notifications" })
        .then((status) => {
          if (cancelled) return;
          permStatus = status;
          permHandler = () => applyState();
          status.addEventListener("change", permHandler);
        })
        .catch(() => {});
    }

    // Tab focus → re-check (covers Safari + manual settings changes).
    const onVisible = () => {
      if (document.visibilityState === "visible") applyState();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      if (permStatus && permHandler) {
        try { permStatus.removeEventListener("change", permHandler); } catch { /* ignore */ }
      }
    };
  }, []);

  // Keep the backend record in sync when the user switches province.
  useEffect(() => {
    if (!hasSub) return;
    updateProvinceForCurrent(selectedProvinceId).catch(() => {});
  }, [selectedProvinceId, hasSub]);

  if (permission === "unsupported") return null;

  const enable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await subscribeToPush({ provinceId: selectedProvinceId });
      setPermission("granted");
      setHasSub(true);
      toast.success(
        "Bildirimler açıldı — son dakika haberlerini kaçırmayacaksınız"
      );
    } catch (e) {
      const msg = e?.message || "Bildirimler açılamadı";
      toast.error(msg);
      setPermission(currentPermission());
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await unsubscribeFromPush();
      setHasSub(false);
      toast.success("Bildirimler kapatıldı");
    } catch {
      toast.error("İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  const onClick = async () => {
    // Re-query the browser truth at click time so a stale React state never
    // blocks the user (real complaint: user re-enabled via browser settings
    // but UI still claimed it was blocked).
    const { permission: live, hasSub: liveSub } = await readBrowserState();
    if (live !== permission) setPermission(live);
    if (liveSub !== hasSub) setHasSub(liveSub);

    if (live === "denied") {
      toast.error(
        "Tarayıcınız bildirimleri engelliyor. Adres çubuğundaki 🔒 (kilit) ikonu → 'İzinler' → 'Bildirimler' bölümünden 'İzin ver'i seçtikten sonra sayfayı yenileyin.",
        { duration: 9000 }
      );
      return;
    }
    if (live === "granted" && !liveSub) {
      // Self-heal even from the click path.
      return enable();
    }
    if (liveSub) return disable();
    return enable();
  };

  let icon;
  let title;
  if (permission === "denied") {
    icon = <BellOff className="w-5 h-5 text-gray-400" />;
    title =
      "Bildirimler tarayıcı tarafından engellenmiş — açmak için tıklayın";
  } else if (hasSub) {
    icon = <BellRing className="w-5 h-5 text-[#e11d2a]" />;
    title = "Bildirimler açık — kapatmak için tıklayın";
  } else {
    icon = <Bell className="w-5 h-5" />;
    title = "Son dakika bildirimleri için tıklayın";
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      title={title}
      aria-label={title}
      data-testid="notification-bell"
      data-permission={permission}
      data-subscribed={hasSub ? "true" : "false"}
      className="p-2 hover:bg-gray-100 rounded-md relative disabled:opacity-50"
    >
      {icon}
      {hasSub && (
        <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#e11d2a]" />
      )}
    </button>
  );
}
